/* Formatting helpers. Every number the UI renders passes through here, so that
   precision rules live in exactly one place. */

const usd2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function compact(n: number): string {
  const a = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (a >= 1e9) return `${sign}${(a / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${sign}${(a / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${sign}${(a / 1e3).toFixed(1)}K`
  return `${sign}${a.toFixed(0)}`
}

/** Roughly three significant figures, so the same helper reads correctly at
    $500 of seed capital and at $500K. */
function moneyDigits(a: number): string {
  if (a >= 1000) return compact(a)
  if (a >= 100) return a.toFixed(0)
  if (a >= 10) return a.toFixed(1)
  return a.toFixed(2)
}

export function compactMoney(n: number): string {
  return `${n < 0 ? '-' : ''}$${moneyDigits(Math.abs(n))}`
}

export function signedCompactMoney(n: number): string {
  const s = n > 0 ? '+' : n < 0 ? '-' : ''
  return `${s}$${moneyDigits(Math.abs(n))}`
}

/** Token prices span many orders of magnitude, so pick precision from value. */
export function price(n: number): string {
  if (n === 0) return '0'
  const a = Math.abs(n)
  if (a >= 1000) return usd2.format(n)
  if (a >= 1) return n.toFixed(3)
  if (a >= 0.01) return n.toFixed(4)
  if (a >= 0.0001) return n.toFixed(6)
  return n.toExponential(2)
}

/** Format `n` with the precision the reference value deserves, so a high/low
    pair never renders at two different precisions. */
export function priceLike(ref: number, n: number): string {
  const a = Math.abs(ref)
  if (a >= 1000) return usd2.format(n)
  if (a >= 1) return n.toFixed(3)
  if (a >= 0.01) return n.toFixed(4)
  if (a >= 0.0001) return n.toFixed(6)
  return n.toExponential(2)
}

export function pct(n: number, decimals = 2): string {
  return `${n.toFixed(decimals)}%`
}

export function signedPct(n: number, decimals = 2): string {
  return `${n > 0 ? '+' : ''}${n.toFixed(decimals)}%`
}

export function qty(n: number): string {
  const a = Math.abs(n)
  if (a >= 1e6) return compact(n)
  if (a >= 100) return n.toFixed(0)
  if (a >= 1) return n.toFixed(2)
  return n.toFixed(4)
}

const hhmmss = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

export const clock = (ts: number): string => hhmmss.format(ts)

export function ago(ts: number, now: number): string {
  const s = Math.max(0, Math.round((now - ts) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}d`
}

export function countdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const d = Math.floor(total / 86400)
  const h = Math.floor((total % 86400) / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const p = (n: number) => String(n).padStart(2, '0')
  return d > 0 ? `${d}D ${p(h)}:${p(m)}:${p(s)}` : `${p(h)}:${p(m)}:${p(s)}`
}


/** Total length of a round as HH:MM:SS, without rolling over into days. */
export function duration(hours: number): string {
  return `${String(Math.floor(hours)).padStart(2, '0')}:00:00`
}
